import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Plus,
  Search,
  Edit,
  Copy,
  FileDown,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react'
import toast from 'react-hot-toast'

import { PageHeader } from '@/components/shared/PageHeader'
import { EmptyState } from '@/components/shared/EmptyState'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { useAuthStore } from '@/store/auth.store'
import { receiptsService } from '@/services/receiptsService'
import { financeService } from '@/services/financeService'
import { Receipt, FinanceCustomer } from '@/api/types'

export const ReceiptsList: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { user } = useAuthStore()

  const [receipts, setReceipts] = useState<Receipt[]>([])
  const [customers, setCustomers] = useState<FinanceCustomer[]>([])
  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [totalCount, setTotalCount] = useState(0)
  const [totalPages, setTotalPages] = useState(0)
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | undefined>(undefined)
  const [advanceAmounts, setAdvanceAmounts] = useState<Record<number, number>>({}) // receiptId -> advanceAmount

  const branchId = user?.selectedBranch?.branchId

  // Load customers for filter
  useEffect(() => {
    if (!branchId) return
    const loadCustomers = async () => {
      try {
        const customerList = await financeService.getAllCustomers(branchId)
        setCustomers(customerList)
      } catch (error: any) {
        // Error handled silently
      }
    }
    loadCustomers()
  }, [branchId])

  // Load receipts
  useEffect(() => {
    if (!branchId) {
      return
    }

    const loadReceipts = async () => {
      try {
        setLoading(true)
        const response = await receiptsService.getAllReceipts(branchId, pageNumber, pageSize)
        const list = response.data || []
        setReceipts(list)
        setTotalCount(response.totalRecords || list.length || 0)
        const pages = response.totalRecords ? Math.ceil(response.totalRecords / response.pageSize) : 0
        setTotalPages(pages || 1)

        // Load advance amounts for receipts that have advanceId
        const receiptsWithAdvance = list.filter((receipt: Receipt) => receipt.advanceId && receipt.advanceId > 0)
        if (receiptsWithAdvance.length > 0) {
          const advanceAmountsMap: Record<number, number> = {}
          
          // Fetch advance lines for each receipt with advanceId
          await Promise.all(
            receiptsWithAdvance.map(async (receipt: Receipt) => {
              if (receipt.receiptId) {
                try {
                  const advanceLines = await receiptsService.getCustomerAdvanceLinesByReceiptId(receipt.receiptId)
                  const totalAdvanceAmount = advanceLines.reduce((sum: number, line: any) => {
                    return sum + (line.amount || 0)
                  }, 0)
                  advanceAmountsMap[receipt.receiptId] = totalAdvanceAmount
                } catch (error) {
                  // If advance lines fetch fails, try to get advance amount from advanceId
                  // For now, set to 0 if we can't fetch
                  advanceAmountsMap[receipt.receiptId] = receipt.advance || 0
                }
              }
            })
          )
          
          setAdvanceAmounts(advanceAmountsMap)
        }
      } catch (error: any) {
        const status = error.response?.status
        const msg = (error.response?.data?.message || '').toLowerCase()
        const isNoData = status === 404 || /not found|no data|no records/.test(msg)
        setReceipts([])
        setTotalCount(0)
        setTotalPages(0)
        if (!isNoData) {
          toast.error(error.response?.data?.message || 'Failed to load receipts')
        }
      } finally {
        setLoading(false)
      }
    }

    loadReceipts()
  }, [branchId, pageNumber, pageSize])

  const filteredReceipts = useMemo(() => {
    return receipts.filter((receipt) => {
      if (selectedCustomerId && receipt.customerId !== selectedCustomerId) {
        return false
      }

      if (!searchQuery.trim()) return true
      const query = searchQuery.toLowerCase()
      return (
        (receipt.receiptNo || '').toLowerCase().includes(query) ||
        (receipt.customerName || '').toLowerCase().includes(query) ||
        (receipt.invoiceNo || '').toLowerCase().includes(query) ||
        (receipt.refNo || '').toLowerCase().includes(query)
      )
    })
  }, [receipts, selectedCustomerId, searchQuery])

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'N/A'
    try {
      const date = new Date(dateString)
      return date.toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
      })
    } catch {
      return dateString
    }
  }

  const formatAmount = (amount?: number) => {
    if (amount === undefined || amount === null) return '0.00'
    return Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
  }

  const handleAddReceipt = () => {
    navigate('/receipts/new')
  }

  const handleEditReceipt = (receipt: Receipt) => {
    const receiptId = receipt.receiptId
    if (!receiptId) {
      toast.error('Invalid receipt ID')
      return
    }
    navigate(`/receipts/${receiptId}/edit`)
  }

  const handleCopy = () => {
    toast.success(t('common.copy') + ' ' + t('common.comingSoon') || 'Copy functionality coming soon')
  }

  const handleCsv = () => {
    toast.success('CSV ' + t('common.export') + ' ' + t('common.comingSoon') || 'CSV export coming soon')
  }

  const handlePageSizeChange = (value: string) => {
    setPageSize(Number(value))
    setPageNumber(1)
  }

  const handleCustomerFilterChange = (value: string) => {
    if (value === 'all') {
      setSelectedCustomerId(undefined)
    } else {
      setSelectedCustomerId(Number(value))
    }
    setPageNumber(1)
  }

  if (!branchId) {
    return (
      <div className="space-y-6 p-6">
        <PageHeader
          title={t('receipts.title')}
          description={t('receipts.manageReceipts')}
        />
        <Card>
          <CardContent className="p-6">
            <EmptyState
              title={t('receipts.branchNotSelected')}
              description={t('receipts.selectBranchToViewReceipts')}
            />
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <PageHeader
        title={t('receipts.title')}
        description={t('receipts.manageReceipts')}
        action={{
          label: t('receipts.addNew'),
          onClick: handleAddReceipt,
          icon: Plus,
        }}
      />

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col sm:flex-row gap-4 items-end">
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('receipts.customer')}</label>
              <Select value={selectedCustomerId ? String(selectedCustomerId) : 'all'} onValueChange={handleCustomerFilterChange}>
                <SelectTrigger>
                  <SelectValue placeholder={t('receipts.all')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('receipts.all')}</SelectItem>
                  {customers.map(customer => (
                    <SelectItem key={customer.customerId} value={String(customer.customerId)}>
                      {customer.customerNameEn || customer.customerNameAr || `Customer ${customer.customerId}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex-1 w-full">
              <label className="text-sm font-medium mb-2 block">{t('receipts.search')}</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
                <Input
                  placeholder={t('receipts.search')}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="flex gap-2 w-full sm:w-auto">
              <Button variant="outline" size="sm" onClick={handleCopy} className="flex-1 sm:flex-initial">
                <Copy className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">Copy</span>
              </Button>
              <Button variant="outline" size="sm" onClick={handleCsv} className="flex-1 sm:flex-initial">
                <FileDown className="h-4 w-4 mr-2" />
                <span className="hidden sm:inline">CSV</span>
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Receipts Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              <p className="mt-4 text-muted-foreground">{t('common.loading')}...</p>
            </div>
          ) : filteredReceipts.length === 0 ? (
            <div className="p-12">
              <EmptyState
                title={searchQuery || selectedCustomerId ? t('receipts.noReceiptsFound') : t('receipts.noReceiptsYet')}
                description={
                  searchQuery || selectedCustomerId
                    ? t('receipts.tryAdjustingSearch')
                    : t('receipts.getStarted')
                }
                action={
                  !searchQuery && !selectedCustomerId
                    ? {
                        label: t('receipts.addNewReceipt'),
                        onClick: handleAddReceipt,
                      }
                    : undefined
                }
              />
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receipts.receiptNo')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receipts.customer')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receipts.date')}
                      </TableHead>
                      <TableHead className="hidden md:table-cell h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receipts.advance')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receipts.amount')}
                      </TableHead>
                      <TableHead className="h-12 px-4 text-center w-[100px] bg-primary/5 font-bold text-primary dark:bg-primary/10 dark:text-primary">
                        {t('receipts.edit')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReceipts.map((receipt) => {
                      const receiptId = receipt.receiptId
                      return (
                        <TableRow key={receiptId} className="border-b hover:bg-primary/5 dark:hover:bg-primary/10 transition-colors">
                          <TableCell className="px-4 py-2 text-sm text-center align-middle font-medium">
                            {receipt.receiptNo || `REC-${receiptId}`}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {receipt.customerName || receipt.customerNameEn || receipt.customerNameAr || 'N/A'}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatDate(receipt.receiptDate)}
                          </TableCell>
                          <TableCell className="hidden md:table-cell px-4 py-2 text-sm text-center align-middle">
                            {receipt.advanceId && receipt.advanceId > 0
                              ? formatAmount(receiptId !== undefined ? (advanceAmounts[receiptId] ?? receipt.advance ?? 0) : (receipt.advance ?? 0))
                              : formatAmount(0)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-sm text-center align-middle">
                            {formatAmount(receipt.amount)}
                          </TableCell>
                          <TableCell className="px-4 py-2 text-center align-middle">
                            <div className="flex justify-center">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleEditReceipt(receipt)}
                                title={t('receipts.editReceipt')}
                                className="h-7 w-7 p-0 hover:bg-primary/10 dark:hover:bg-primary/20"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="border-t px-4 sm:px-6 py-4 flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="text-sm text-muted-foreground text-center sm:text-left">
                  Showing {(pageNumber - 1) * pageSize + 1} to{' '}
                  {Math.min(pageNumber * pageSize, totalCount)} of {totalCount} entries
                </div>
                <div className="flex items-center gap-2 flex-wrap justify-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-muted-foreground whitespace-nowrap">Page Size:</span>
                    <Select value={String(pageSize)} onValueChange={handlePageSizeChange}>
                      <SelectTrigger className="w-[100px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="10">10</SelectItem>
                        <SelectItem value="20">20</SelectItem>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => Math.max(prev - 1, 1))}
                    disabled={pageNumber === 1}
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span className="hidden sm:inline">Previous</span>
                  </Button>
                  <div className="text-sm whitespace-nowrap">
                    Page {pageNumber} of {totalPages || 1}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPageNumber((prev) => (prev < totalPages ? prev + 1 : prev))}
                    disabled={pageNumber >= totalPages}
                  >
                    <span className="hidden sm:inline">Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

